import type { VisitDisplay, VisitFieldSuggestions } from "../types";
import { collapseWhitespace, normalizeGroupKey } from "./visitSorting";

type FieldAccessor = (visit: VisitDisplay) => string | null | undefined;

interface FieldBucket {
  total: number;
  spellings: Map<string, number>;
}

// No `sensitivity: "base"` here: bucket keys are already lowercased via normalizeGroupKey, so
// within a bucket ties must break between spellings differing only by case, not compare equal.
const compareLabels = (a: string, b: string): number => a.localeCompare(b);

/**
 * Returns the distinct values of one visit field, most-used first.
 *
 * - Spellings differing only by case share a bucket; the label is its most frequent spelling
 *   after whitespace normalization, so it may not appear verbatim in any single visit
 *   (e.g. "Dr.   Smith" normalizes to "Dr. Smith").
 * - Both the label election and the final ordering break ties alphabetically, so the result
 *   depends only on the set of visits and never on the order they arrive in.
 */
const rankFieldValues = (visits: VisitDisplay[], accessor: FieldAccessor): string[] => {
  const buckets = new Map<string, FieldBucket>();

  for (const visit of visits) {
    const raw = accessor(visit);
    const value = collapseWhitespace(raw);
    if (!value) continue;

    const key = normalizeGroupKey(raw);
    const bucket = buckets.get(key) ?? { total: 0, spellings: new Map<string, number>() };
    bucket.total += 1;
    bucket.spellings.set(value, (bucket.spellings.get(value) ?? 0) + 1);
    buckets.set(key, bucket);
  }

  const ranked = [...buckets.values()].map((bucket) => {
    let label = "";
    let labelCount = 0;
    for (const [spelling, count] of bucket.spellings) {
      if (count > labelCount || (count === labelCount && compareLabels(spelling, label) < 0)) {
        label = spelling;
        labelCount = count;
      }
    }
    return { label, total: bucket.total };
  });

  ranked.sort((a, b) => b.total - a.total || compareLabels(a.label, b.label));
  return ranked.map((entry) => entry.label);
};

export const buildVisitFieldSuggestions = (visits: VisitDisplay[]): VisitFieldSuggestions => ({
  providers: rankFieldValues(visits, (visit) => visit.provider_name),
  facilities: rankFieldValues(visits, (visit) => visit.facility_name),
  subtypes: rankFieldValues(visits, (visit) => visit.visit_subtype),
});
